<template>
  <!-- @touchmove 兜底：iOS Safari 不吃 body{overflow:hidden}，只能靠 preventDefault 拦 -->
  <div
    v-if="visible"
    class="create-ws-overlay"
    @click.self="onCancel"
    @touchmove="onOverlayTouchMove"
  >
    <div class="create-ws-dialog">
      <div class="create-ws-dialog-header">
        <h3 class="create-ws-dialog-title">{{ dialogTitle }}</h3>
        <button class="create-ws-dialog-close" @click="onCancel">×</button>
      </div>
      <div ref="body" class="create-ws-dialog-body">
        <div class="create-ws-form-stack">
          <div class="create-ws-field">
            <label class="create-ws-label">工作表名称 <span class="create-ws-required">*</span></label>
            <input
              type="text"
              class="create-ws-input"
              v-model="form.name"
              placeholder="请输入工作表名称"
              maxlength="50"
              @keyup.enter="onSubmit"
            />
            <div class="create-ws-hint">{{ form.name.length }}/50</div>
          </div>

          <div class="create-ws-field create-ws-field-description">
            <label class="create-ws-label">描述</label>
            <textarea
              class="create-ws-textarea"
              v-model="form.description"
              placeholder="请输入工作表描述（选填）"
              maxlength="500"
              rows="4"
            ></textarea>
            <div class="create-ws-hint">{{ form.description.length }}/500</div>
          </div>

          <div class="create-ws-field">
            <label class="create-ws-label">图标</label>
            <div class="create-ws-icon-type-selector">
              <label
                :class="['create-ws-radio-option', { active: form.iconType === 'preset' }]"
                @click="form.iconType = 'preset'"
              >
                <span class="create-ws-radio-circle">
                  <span v-if="form.iconType === 'preset'" class="create-ws-radio-dot"></span>
                </span>
                <span class="create-ws-radio-label">预设</span>
              </label>
              <label
                :class="['create-ws-radio-option', { active: form.iconType === 'image' }]"
                @click="form.iconType = 'image'"
              >
                <span class="create-ws-radio-circle">
                  <span v-if="form.iconType === 'image'" class="create-ws-radio-dot"></span>
                </span>
                <span class="create-ws-radio-label">图片</span>
              </label>
              <label
                :class="['create-ws-radio-option', { active: form.iconType === 'letter' }]"
                @click="form.iconType = 'letter'"
              >
                <span class="create-ws-radio-circle">
                  <span v-if="form.iconType === 'letter'" class="create-ws-radio-dot"></span>
                </span>
                <span class="create-ws-radio-label">字母</span>
              </label>
            </div>

            <!-- 预设图标 -->
            <div v-if="form.iconType === 'preset'" class="create-ws-icon-grid">
              <div
                v-for="icon in presetIcons"
                :key="icon.name"
                :class="['create-ws-icon-option', { active: form.iconClass === icon.class }]"
                :style="iconOptionStyle"
                @click="form.iconClass = icon.class"
              >
                <i :class="icon.class"></i>
              </div>
            </div>

            <!-- 图片上传 -->
            <div v-else-if="form.iconType === 'image'" class="create-ws-icon-image-preview">
              <div class="create-ws-image-avatar-wrapper">
                <div
                  class="create-ws-image-avatar"
                  :class="{ 'create-ws-image-avatar-empty': !imagePreview }"
                  @click="triggerFileInput"
                >
                  <img v-if="imagePreview" :src="imagePreview" />
                  <div v-if="imagePreview" class="create-ws-image-avatar-overlay">
                    <i class="fas fa-camera"></i>
                  </div>
                  <i v-else class="fas fa-camera create-ws-image-avatar-icon"></i>
                </div>
                <input
                  ref="fileInput"
                  type="file"
                  accept="image/jpeg,image/png,image/jpg"
                  style="display:none"
                  @change="handleImageChange"
                />
              </div>
              <span class="create-ws-image-hint">点击上传图片</span>
            </div>

            <!-- 首字母 -->
            <div v-else class="create-ws-icon-letter-preview">
              <div class="create-ws-icon-letter-display" :style="{ color: selectedColor.active }">
                {{ firstLetter }}
              </div>
              <span class="create-ws-icon-letter-hint">使用名称首字母</span>
            </div>
          </div>

          <div class="create-ws-field">
            <label class="create-ws-label">色系</label>
            <div class="create-ws-color-picker">
              <div
                v-for="c in colorPalette"
                :key="c.key"
                :class="['create-ws-color-dot', { active: form.colorKey === c.key }]"
                :style="{ background: c.active, '--dot-color-dark': c.dark }"
                :title="c.label"
                @click="form.colorKey = c.key"
              >
                <!-- 图标取 Font Awesome 图标库，禁止手写 <svg> -->
                <FaIcon v-if="form.colorKey === c.key" :icon="faCheck" :size="16" />
              </div>
            </div>
          </div>

          <div class="create-ws-field-actions">
            <button class="create-ws-cancel-btn" @click="onCancel">取消</button>
            <button
              class="create-ws-confirm-btn"
              :disabled="!canSubmit || submitting"
              @click="onSubmit"
            >
              {{ confirmText }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { createProject, updateProject } from '@/api/project.js'
import { shadeColor, hexToRgba } from './profileStorage.js'
import FaIcon from '@/components/common/FaIcon.vue'
// 图标一律走 Font Awesome 图标库（子路径引入），禁止手写 <svg> 画图标
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck'

/**
 * 预设图标（11 个，一行排开）。
 * 2026-10-03 中途删过 folder / chart-line 变 8 个，后恢复 10 个，最后又加 tag 成 11 个。
 * ⚠️ 若再增删，必须同步下面 `.create-ws-icon-grid` 的 `repeat(N, …)` 列数，
 *    以及格子宽度 —— 弹窗内容区只有 500px（560 总宽 − 30×2 padding，全局
 *    box-sizing:border-box）。图标格与色点统一 32px、间隔 12px：图标 11×32 + 10×12 = 472px，
 *    色板 11 点同宽 472px，两排完全对齐、都 ≤ 500px 单行放得下。
 */
export const PRESET_ICONS = [
  { name: 'list-check', class: 'fas fa-list-check' },
  { name: 'calendar-check', class: 'fas fa-calendar-check' },
  { name: 'note-sticky', class: 'fas fa-note-sticky' },
  { name: 'users', class: 'fas fa-users' },
  { name: 'clock', class: 'fas fa-clock' },
  { name: 'flag', class: 'fas fa-flag' },
  { name: 'lightbulb', class: 'fas fa-lightbulb' },
  { name: 'star', class: 'fas fa-star' },
  { name: 'folder', class: 'fas fa-folder' },
  { name: 'chart-line', class: 'fas fa-chart-line' },
  { name: 'tag', class: 'fas fa-tag' }
]

/**
 * 「弹窗与站点背景的滚动互斥规则」，集中说一次，改之前先读完：
 *
 * 本组件与 TaskBlockPage 是**两层各自独立的滚动体系**，谁锁谁必须分开看：
 *
 *   站点外层（TaskBlockPage）
 *     ├─ .tb-header        欢迎区：高度由 JS 手势驱动（270/210 → 0），**自研折叠**
 *     └─ .main             内容列：overflow-y:auto，**浏览器原生滚动**
 *
 *   弹窗（本组件，DOM 挂在 .frame 内部，position:fixed 只影响绘制不影响事件路径）
 *     └─ .create-ws-dialog-body   自己滚自己的，隐藏滚动条但保留滚动能力
 *
 * 打开弹窗要同时挡住上面两路，缺一路就会出现「背景还在动」：
 *
 *   1) 自研折叠手势 —— 事件源是 .frame 上 el.addEventListener('wheel'/'touchmove')
 *      挂的**原生监听**，滚轮在弹窗里滚动时事件照常冒泡到 .frame，于是背景
 *      欢迎区跟着折叠。拦法：本组件 emit('visible-change')，父组件置
 *      dialogScrollLock，直接在 onWheel / onTouchMove 里 return（**不能**
 *      preventDefault，否则弹窗自己也滚不了）。
 *
 *   2) 原生滚动 —— body{overflow:hidden} 只能管住页面级滚动，管不住 .main
 *      这个内层 overflow-y:auto 容器。拦法：父组件把 .main 的 overflow
 *      落成 hidden（见 applyDialogScrollLock）。
 *
 * 本组件自己这边（backgroundSilent，默认开）是第三道：body 锁 + 滚动条占位补偿 +
 * overscroll-behavior:contain + 落点不在内容区的 touchmove 兜底（iOS Safari
 * 不吃 body{overflow:hidden}）。这三道合起来，「弹窗打开 → 只有弹窗能滚」。
 *
 * ⚠️ 结论：别只改一边。删掉 (1) 或 (2) 任意一路，背景就又能被滚走。
 */

/**
 * 饱和度削减比例 —— **只作用于描边与大面积底色，主色本身不降**。
 *
 * 为什么分两套：主色（色板圆圈 / 图标 / 主按钮 /「今天」徽章）是**小面积点缀**，
 * 要的就是鲜艳有辨识度，降了会发灰发闷；但它一旦被铺成 3px 板块外框、
 * 表头描边、整块底色，高饱和就会非常跳、很刺眼。
 * 所以这里保留 active 的原饱和度，另派生一个 muted（向灰度收敛 35%），
 * 专门给 border / borderStrong / soft / soft2 / bg / surface2 / dark 用。
 * 想让描边再淡/再浓，只改这一个数。
 */
const SATURATION_REDUCTION = 0.35

/** 按亮度加权转灰度后向原色回插，amount=0 不变、1 变纯灰。 */
function desaturate(hex, amount) {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = (num >> 16) & 255
  const g = (num >> 8) & 255
  const b = num & 255
  const gray = 0.299 * r + 0.587 * g + 0.114 * b
  const mix = c => Math.round(gray + (c - gray) * (1 - amount))
  return '#' + (0x1000000 + mix(r) * 0x10000 + mix(g) * 0x100 + mix(b)).toString(16).slice(1)
}

/**
 * 半透明色 → **等效实色**：把 hex 按 alpha 兑到白底上，算出「叠在白卡上」的那个实色 hex。
 *
 * 用途：色板派生出的 soft / soft2 / border / borderStrong / surface2 全走这里。
 * 它们都是 alpha 很低的浅色，用 rgba 铺开时几乎等于纯白，且会透出背后的东西
 * （页面底色本身是半透明 + 壁纸），于是非默认色系下这些块看着像「透明没了底色」。
 * 兑白成实色后观感不变、但绝不再透。
 */
function tintOverWhite(hex, alpha) {
  const num = parseInt(hex.replace('#', ''), 16)
  const mix = c => Math.round(255 - (255 - c) * alpha)
  const r = mix((num >> 16) & 255)
  const g = mix((num >> 8) & 255)
  const b = mix(num & 255)
  return '#' + (0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)
}

/**
 * 默认色（色板第一项）。
 * active 取 App.vue 里 --lp-primary 的默认值 #4aa46a，且带 isDefault 标记：
 * 选中它时父组件**不覆盖**任何 --lp-* 变量，整个表格走回原本的浅绿默认主题。
 */
const DEFAULT_PALETTE_ACTIVE = '#4aa46a'

/**
 * 工作表色板（默认 + 10 种配色）。
 *
 * 每个主色派生出两套色：
 *   ── 主色系（**原饱和度，鲜艳**）── 小面积点缀
 *   - active       ：主色（提交给后端；色板圆圈 / 图标 / 主按钮 /「今天」徽章）
 *   - text         ：备用字段（active -30%，当前未使用）
 *
 *   ── 柔和系（**降饱和 35%**，一律兑成**不透明实色**）── 描边与大面积，铺开后不刺眼
 *   - dark         ：深主色（muted -25%），用于文字、hover —— 保证白底可读
 *   - soft         ：浅背景（兑白 0.12），hover / 分类 chips / 「今天」列表头
 *   - soft2        ：次浅背景（兑白 0.28），active 行 / 次级背景
 *   - border       ：普通边框（兑白 0.20），内部分隔线、输入框
 *   - borderStrong ：强边框（兑白 0.45），板块外框 / 时间表外框
 *                    ⚠️ 兑白后的**浅色**而不是不透明深色：它要画 3px 的板块外框，
 *                    深色会显得又重又闷
 *   - bg           ：页面底色（alpha 0.06），极淡 —— **唯一保留半透明**的一个，
 *                    因为它整片铺在壁纸上，就是要让壁纸透出来
 *   - surface2     ：次表面（兑白 0.07），偶数行 / 折叠行 / 周累计表表头
 *
 * ⚠️ **为什么要兑白成实色而不是直接用 rgba**（2026-10-06 修复）：
 *   这些令牌绝大多数被当成**实底**用（周累计表表头 = --lp-surface-2、
 *   「今天」列表头 = --lp-primary-soft、斑马纹、板块外框、表头分隔线……）。
 *   rgba 铺在白卡上看着还算正常，可一旦背后不是纯白（页面底色是半透明、
 *   底下透着壁纸），它就会**连同壁纸一起透出来**，表现就是「非默认色系下
 *   这些块像透明的一样没有底色」。默认色系不覆盖 --lp-*，走 App.vue 的实色
 *   （#e9f5ec / #f7fbf8 / #e3efe6），所以只有非默认色系会露馅。
 *   兑到白底上算出的实色与「rgba 叠白」的观感**完全一致**，只是不再透明。
 *
 * 刻意**不动**的令牌：--lp-surface（保持纯白，否则整卡变色文字难读）、
 * --lp-text / -2 / -3（保持中性灰，跟随色系会让正文失去对比度）。
 */
function buildPaletteEntry(key, label, rawActive, opts = {}) {
  // 主色保持原饱和度：色板圆圈、图标要的就是鲜艳有辨识度
  const active = rawActive
  // 描边 / 大面积底色 / 文字用降饱和版本，避免高饱和铺开后发跳
  const muted = opts.isDefault ? rawActive : desaturate(rawActive, SATURATION_REDUCTION)
  return {
    key,
    label,
    active,
    muted,
    isDefault: !!opts.isDefault,
    text: shadeColor(active, -30),
    dark: shadeColor(muted, -25),
    soft: tintOverWhite(muted, 0.12),
    soft2: tintOverWhite(muted, 0.28),
    border: tintOverWhite(muted, 0.2),
    borderStrong: tintOverWhite(muted, 0.45),
    // 页面底色刻意保持半透明：它整片铺在壁纸上，透出壁纸才是要的效果
    bg: hexToRgba(muted, 0.06),
    surface2: tintOverWhite(muted, 0.07)
  }
}

const COLOR_PALETTE = [
  buildPaletteEntry('default', '默认', DEFAULT_PALETTE_ACTIVE, { isDefault: true }),
  buildPaletteEntry('work', '工作', '#5DB7F5'),
  buildPaletteEntry('study', '学习', '#63D391'),
  buildPaletteEntry('research', '科研', '#45C7C0'),
  buildPaletteEntry('programming', '编程', '#9B7FEA'),
  buildPaletteEntry('reading', '阅读', '#F28DBB'),
  buildPaletteEntry('sport', '运动', '#55D6A0'),
  buildPaletteEntry('entertainment', '娱乐', '#FF9D66'),
  buildPaletteEntry('life', '生活', '#F5C84B'),
  buildPaletteEntry('social', '社交', '#F47F91'),
  buildPaletteEntry('rest', '休息', '#829CE8')
]

/**
 * 暴露给父组件的色板：传入工作表的 hex 时反查 key（取最接近的派生色），
 * 或直接拿来做主题色派生（不再硬编码 hex）。
 */
export const PROJECT_PALETTE = COLOR_PALETTE

export default {
  name: 'CreateWorkspaceDialog',
  components: { FaIcon },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    workspaceId: {
      type: [Number, String],
      default: 1
    },
    mode: {
      type: String,
      default: 'create',
      validator: v => ['create', 'edit'].includes(v)
    },
    project: {
      type: Object,
      default: () => null
    },
    /**
     * 「是否让背景静默」开关，默认开。
     * 本组件挂在 .frame 内部，而 TaskBlockPage 在 .frame 上挂了 window 级的
     * wheel / touchmove 监听做「下滑折叠欢迎区」手势 —— 事件会从弹窗冒泡上去，
     * 导致弹窗打开时背景还能被滚走。自己往外 emit 事件太绕（要穿过 .frame 上
     * 那个直接 addEventListener 的原生监听），所以改由父组件监听本事件，
     * 直接闭掉那套手势（见 TaskBlockPage.vue 的 @visible-change）。
     *
     * 命名避开 lockScroll/unlockScroll —— 那是下面 methods 里管 body 锁的方法名，
     * 撞名会被 eslint 的 vue/no-dupe-keys 拦下。
     */
    backgroundSilent: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      presetIcons: PRESET_ICONS,
      colorPalette: COLOR_PALETTE,
      // Font Awesome 图标定义（模板 :icon 用）
      faCheck,
      submitting: false,
      imageFile: null,
      imagePreview: '',
      form: {
        name: '',
        description: '',
        iconType: 'preset',
        iconClass: PRESET_ICONS[0].class,
        colorKey: COLOR_PALETTE[0].key
      }
    }
  },
  computed: {
    isEdit() {
      return this.mode === 'edit'
    },
    dialogTitle() {
      return this.isEdit ? '编辑工作表' : '新建工作表'
    },
    confirmText() {
      return this.submitting ? (this.isEdit ? '保存中...' : '创建中...') : (this.isEdit ? '保存' : '创建')
    },
    selectedColor() {
      return this.colorPalette.find(c => c.key === this.form.colorKey) || COLOR_PALETTE[0]
    },
    firstLetter() {
      return this.form.name.trim().charAt(0) || '工'
    },
    canSubmit() {
      const name = this.form.name.trim()
      return name.length >= 2 && name.length <= 50
    },
    iconOptionStyle() {
      // 10 种配色都是高饱和度浅色，直接用 active 在白底都够清楚，
      // 不要再走 text（-30% 深色）——会把粉色压成暗玫红，跟色板对不上。
      // 选中色系圆圈也是用 active 渲染的，这样图标和色板视觉完全匹配。
      //
    // 当前图标的选中态只靠 --dot-color-soft 浅底色区分（用户已去掉选中边框），
   // 不再消费 --dot-color-dark；该变量只供色板圆圈（虚线选框）使用。
   // 保留 color + --dot-color-soft 注入即可。
   return {
     color: this.selectedColor.active,
     '--dot-color-soft': this.selectedColor.soft
   }
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) this.resetForm()
        // 开/关都要通知父组件：父组件据此启停 .frame 上的折叠手势 ——
        // 那个手势是原生 addEventListener 挂的，事件从弹窗冒泡上去照收不误，
        // 不管住的话「弹窗打开时背景还能被滚走」。
        this.$emit('visible-change', val, this.backgroundSilent)
      }
    }
  },
  mounted() {
    // 弹窗可能以 visible=true 的初始状态挂载（例如父组件延迟渲染），补一次锁
    if (this.visible) this.lockScroll()
  },
  beforeDestroy() {
    // 组件被销毁时（如切换页面）必须解锁，否则 body 会永久锁死滚动
    this.unlockScroll()
  },
  methods: {
    /**
     * 打开弹窗期间**只让弹窗自己能滚**，背后页面一律锁死。
     *
     * 三道防线（缺一不可）：
     *  1. body{overflow:hidden} —— 最常见的做法，挡住滚轮 / 键盘 / 滚动条拖拽；
     *     原值先存下来，关闭时还原成"上一个状态"而不是硬写 ''，
     *     这样嵌套场景（比如全屏表格 TimeTable.vue 也锁过 body）不会互相踩。
     *  2. 滚动条占位补偿 —— 隐藏滚动条会让可视宽度变宽、页面横向"跳"一下，
     *     用 padding-right 把滚动条宽度补回去。
     *  3. overscroll-behavior:contain（见 style）—— 内容区滚到顶/底后不再把
     *     滚动"传染"给外层；配合下面的 touchmove 兜底 iOS。
     */
    lockScroll() {
      if (this.scrollLocked) return
      const body = document.body
      this.savedOverflow = body.style.overflow
      this.savedPaddingRight = body.style.paddingRight

      const gutter = window.innerWidth - document.documentElement.clientWidth
      if (gutter > 0) {
        const current = parseFloat(window.getComputedStyle(body).paddingRight) || 0
        body.style.paddingRight = `${current + gutter}px`
      }
      body.style.overflow = 'hidden'
      this.scrollLocked = true
    },
    unlockScroll() {
      if (!this.scrollLocked) return
      document.body.style.overflow = this.savedOverflow || ''
      document.body.style.paddingRight = this.savedPaddingRight || ''
      this.scrollLocked = false
    },
    /**
     * 触摸兜底：iOS Safari 对 body{overflow:hidden} 不敏感，页面照样能拖。
     * 规则很简单 —— 落点不在弹窗内容区里的一律 preventDefault；
     * 落在内容区里的放它自己滚（滚到头由 overscroll-behavior 兜住，
     * 老版 Safari 没有该属性时，body 锁 + 外层不可滚也已经封死）。
     */
    onOverlayTouchMove(e) {
      const scroller = this.$refs.body
      if (!scroller || !scroller.contains(e.target)) {
        e.preventDefault()
      }
    },
    resetForm() {
      if (this.isEdit && this.project) {
        const icon = this.project.icon || ''
        const preset = PRESET_ICONS.find(i => i.name === icon)
        let iconType = 'letter'
        let iconClass = preset ? preset.class : PRESET_ICONS[0].class
        if (preset) {
          iconType = 'preset'
          iconClass = preset.class
        } else if (icon === 'image') {
          iconType = 'image'
        }
        const colorKey = COLOR_PALETTE.find(c => c.active === this.project.color)?.key || COLOR_PALETTE[0].key
        this.form = {
          name: this.project.name || '',
          description: this.project.description || '',
          iconType,
          iconClass,
          colorKey
        }
        this.imageFile = null
        this.imagePreview = ''
      } else {
        this.form = {
          name: '',
          description: '',
          iconType: 'preset',
          iconClass: PRESET_ICONS[0].class,
          colorKey: COLOR_PALETTE[0].key
        }
        this.imageFile = null
        this.imagePreview = ''
      }
      this.submitting = false
    },
    onCancel() {
      this.$emit('close')
    },
    triggerFileInput() {
      this.$refs.fileInput.click()
    },
    handleImageChange(event) {
      const file = event.target.files[0]
      if (!file) return

      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg']
      if (!allowedTypes.includes(file.type)) {
        alert('请上传 JPG 或 PNG 格式的图片')
        return
      }
      if (file.size > 2 * 1024 * 1024) {
        alert('图片大小不能超过 2MB')
        return
      }

      this.imageFile = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    async onSubmit() {
      if (!this.canSubmit || this.submitting) return

      const name = this.form.name.trim()
      const description = this.form.description.trim()
      const color = this.selectedColor

      // 将前端图标映射为后端 ProjectCmd 所需的 icon 字符串
      let icon = ''
      if (this.form.iconType === 'preset') {
        const matched = this.presetIcons.find(i => i.class === this.form.iconClass)
        icon = matched ? matched.name : 'book'
      } else if (this.form.iconType === 'letter') {
        icon = this.firstLetter
      } else if (this.form.iconType === 'image') {
        icon = 'image'
      }

      this.submitting = true
      try {
        let res
        if (this.isEdit) {
          res = await updateProject(this.project.id, {
            name,
            icon,
            color: color.active,
            description
          })
        } else {
          res = await createProject({
            workspaceId: this.workspaceId,
            name,
            icon,
            color: color.active,
            description
          })
        }
        if (!res.success || res.code !== 200) {
          alert(res.message || (this.isEdit ? '保存失败' : '创建工作表失败'))
          return
        }
        this.$emit(this.isEdit ? 'updated' : 'created', res.data)
        this.onCancel()
      } catch (e) {
        alert('网络异常，请稍后重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.create-ws-overlay {
  /* 使用 !important 确保覆盖父容器可能设置的 z-index */
  position: fixed !important;
  inset: 0;
  background: rgba(45, 55, 72, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000 !important;
  padding: 1rem;
  font-family: 'DynaPuff', 'OPPOSans', Avenir, Helvetica, Arial, sans-serif;
  /* 遮罩自己不滚，也别把滚动传给后面的页面（overscroll-behavior:none
     = 既不带动外层，也不触发下拉刷新之类的浏览器行为） */
  overscroll-behavior: none;
}

.create-ws-dialog {
  position: relative;
  width: 560px;
  max-width: calc(100vw - 32px);
  max-height: 90vh;
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid #ebeef5;
  padding: 1.875rem;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  font-family: 'DynaPuff', 'OPPOSans', Avenir, Helvetica, Arial, sans-serif;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.create-ws-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1.875rem;
  padding: 0;
  flex-shrink: 0;
}

.create-ws-dialog-title {
  font-size: 1.375rem;
  font-weight: 500;
  color: #2d3748;
  margin: 0;
}

.create-ws-dialog-close {
  position: absolute;
  top: 1rem;
  right: 1.25rem;
  border: none;
  background: transparent;
  font-size: 1.5rem;
  color: #a0aec0;
  cursor: pointer;
  line-height: 1;
  width: auto;
  height: auto;
  border-radius: 0;
  display: block;
  padding: 0;
}

.create-ws-dialog-close:hover {
  color: #718096;
  background: transparent;
}

/* 内容区：窗口很矮时仍能滚动，但**隐藏纵向 + 横向滚动条**。
   只隐藏视觉（scrollbar-width / -ms-overflow-style / ::-webkit-scrollbar），
   overflow 仍保留 auto —— 滚轮、触摸、键盘照样能滚，只是不显示那条轨道。
   写法与项目内 .main（TaskBlockPage.vue）、.tablebox（TimeTable.vue）保持一致。

   overscroll-behavior:contain 是「滚动只作用于本弹窗」的关键：
   内容滚到顶/底后不再把滚动链条传给外层（否则会带动背后的页面一起动）。 */
.create-ws-dialog-body {
  padding: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
  flex: 1;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.create-ws-dialog-body::-webkit-scrollbar {
  display: none;
}

.create-ws-form-stack {
  display: flex;
  flex-direction: column;
  gap: 1.563rem;
}

.create-ws-field {
  margin-bottom: 0;
}

.create-ws-field-description .create-ws-textarea {
  min-height: 90px;
  resize: vertical;
}

.create-ws-label {
  display: block;
  margin-bottom: 0.625rem;
  color: #666;
  font-size: 0.875rem;
  font-weight: 500;
}

.create-ws-required {
  color: #c45c5c;
}

.create-ws-input,
.create-ws-textarea {
  width: 100%;
  font-family: inherit;
  font-size: 0.875rem;
  color: #2d3748;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 0.25rem;
  padding: 0.625rem 0.875rem;
  outline: none;
  transition: border-color 0.18s, background 0.18s, box-shadow 0.18s;
  box-sizing: border-box;
}

.create-ws-input:focus,
.create-ws-textarea:focus {
  border-color: #409eff;
  background: #fff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.2);
}

.create-ws-input::placeholder,
.create-ws-textarea::placeholder {
  color: #a0aec0;
}

/* 描述框自己的滚动条同样隐藏（文字超过 4 行时仍可滚动），
   否则弹窗整体无滚动条、唯独文本域露出一条会很突兀。
   同样加 contain：描述滚到底后不要接着滚外层弹窗内容。 */
.create-ws-textarea {
  resize: vertical;
  min-height: 72px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.create-ws-textarea::-webkit-scrollbar {
  display: none;
}

.create-ws-hint {
  font-size: 0.75rem;
  color: #909399;
  text-align: right;
  margin-top: 0.25rem;
}

.create-ws-field-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.625rem;
  margin-top: 1.875rem;
  padding-top: 0;
}

.create-ws-cancel-btn {
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #606266;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 0.25rem;
  padding: 0.625rem 1.25rem;
  cursor: pointer;
  transition: all 0.15s;
}

.create-ws-cancel-btn:hover {
  color: #409eff;
  border-color: #c6e2ff;
  background: #ecf5ff;
}

.create-ws-confirm-btn {
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  background: #409eff;
  border: 1px solid #409eff;
  border-radius: 0.25rem;
  padding: 0.625rem 1.25rem;
  cursor: pointer;
  transition: all 0.15s;
  box-shadow: none;
  position: static;
  overflow: visible;
}

.create-ws-confirm-btn:hover:not(:disabled) {
  background: #66b1ff;
  border-color: #66b1ff;
  transform: none;
  box-shadow: none;
}

.create-ws-confirm-btn:active:not(:disabled) {
  transform: none;
  box-shadow: none;
}

.create-ws-confirm-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  box-shadow: none;
}

/* 图标类型选择器 */
.create-ws-icon-type-selector {
  display: flex;
  gap: 16px;
  margin-bottom: 12px;
}

.create-ws-radio-option {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.15s;
}

.create-ws-radio-circle {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #dcdfe6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
  flex-shrink: 0;
}

.create-ws-radio-option:hover .create-ws-radio-circle {
  border-color: #409eff;
}

.create-ws-radio-option.active .create-ws-radio-circle {
  border-color: #409eff;
}

.create-ws-radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #409eff;
}

.create-ws-radio-label {
  font-size: 14px;
  color: #606266;
  transition: all 0.15s;
}

.create-ws-radio-option.active .create-ws-radio-label {
  color: #409eff;
  font-weight: 600;
}

/* 预设图标网格（11 个一行，与色板圆点等大 32px）
   列宽固定为图标宽（不再 1fr）+ justify-content 居中：之前 1fr 让每列
   撑满到容器宽，图标在格里居中后视觉间隔大得像 ~22px，排起来又空又跳。
   改成固定列宽后图标紧贴、间隔由 gap 控住，整组在容器里居中。
   ⚠️ 宽度是算出来的：弹窗 560px − padding 30×2 = 500px 可用（全局
   box-sizing:border-box），11×32 + 10×12 = 472px，与色板 11 点同宽、完全对齐。
   图标格与色点统一 32px、间隔也统一 12px，两排都 ≤ 500px 内单行。 */
.create-ws-icon-grid {
  display: grid;
  grid-template-columns: repeat(11, 32px);
  gap: 12px;
  justify-content: center;
}

.create-ws-icon-option {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;
  font-size: 24px;
  background: transparent;
}

.create-ws-icon-option:hover {
  transform: scale(1.05);
  filter: brightness(1.05);
}

.create-ws-icon-option.active {
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.35);
}

/* 首字母预览 */
.create-ws-icon-letter-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 0.5rem;
  border: 1px solid #ebeef5;
}

.create-ws-icon-letter-display {
  width: 48px;
  height: 48px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  background: transparent;
}

.create-ws-icon-letter-hint {
  font-size: 13px;
  color: #909399;
}

/* 图片上传 */
.create-ws-icon-image-preview {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f5f7fa;
  border-radius: 0.5rem;
  border: 1px solid #ebeef5;
}

.create-ws-image-avatar {
  width: 48px;
  height: 48px;
  border-radius: 0.5rem;
  overflow: hidden;
  position: relative;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  transition: all 0.15s;
}

.create-ws-image-avatar:hover {
  border-color: #409eff;
}

.create-ws-image-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.create-ws-image-avatar-empty {
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #dcdfe6;
}

.create-ws-image-avatar-icon {
  font-size: 20px;
  color: #909399;
}

.create-ws-image-avatar-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(45, 55, 72, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.15s;
  color: #fff;
  font-size: 16px;
}

.create-ws-image-avatar:hover .create-ws-image-avatar-overlay {
  opacity: 1;
}

.create-ws-image-hint {
  font-size: 13px;
  color: #909399;
}

/* 色系选择器（11 个圆圈色点，与图标格等大 32px、同一行排开）
   用 grid 与图标网格对齐同款节奏：repeat(11,32px) + gap:12px + 居中，
   11×32 + 10×12 = 472px ≤ 500px 内容区，单行不换行。
   gap 取 12px 是用户认可的"最初宽松间隔"（图标的 4px 格间保持紧凑、各取所需）。
   注：色点尺寸在 2026-10-03 曾被临时放大到 40px，用户反馈"太大、还是
   之前 32px 合适"，已还原回 32px；间隔也曾被压到 4px 统一，用户要求还原 12px。 */
.create-ws-color-picker {
  display: grid;
  grid-template-columns: repeat(11, 32px);
  gap: 12px;
  justify-content: center;
}

.create-ws-color-dot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s;
  border: 2px solid transparent;
}

.create-ws-color-dot:hover {
  transform: scale(1.1);
}

.create-ws-color-dot.active {
  border-color: #fff;
  box-shadow: 0 0 0 2px #409eff;
}

.create-ws-color-dot svg {
  width: 16px;
  height: 16px;
}

@media (max-width: 768px) {
  .create-ws-dialog {
    max-height: 95vh;
  }
}

/* ===== 轻量扁平主题覆盖（参考稿配色）=====
   只改颜色 / 描边 / 圆角 / 阴影，布局、尺寸、间距、字号与 @media 数值全部沿用上面的原值。 */
/* —— 弹窗外壳 —— */
.create-ws-overlay {
  background: rgba(38, 51, 43, 0.35);
}
.create-ws-dialog {
  background: var(--lp-surface, #fff);
  border: 1px solid var(--lp-border, #e3efe6);   /* 2026-10-04：去掉 3px 绿系描边，改极淡中性描边 */
  border-radius: 1rem;
  box-shadow: var(--lp-shadow-lg, 0 8px 24px rgba(52, 102, 68, 0.08));
}
.create-ws-dialog-title {
  color: var(--lp-text, #26332b);
}
.create-ws-dialog-close {
  color: var(--lp-text-3, #94a59b);
}
.create-ws-dialog-close:hover {
  color: var(--lp-text-2, #5f7268);
}

/* —— 表单：标签 / 输入框 / 文本域 / 提示 —— */
.create-ws-label {
  color: var(--lp-text-2, #5f7268);
}
.create-ws-required {
  color: #d9534f;
}
.create-ws-input,
.create-ws-textarea {
  color: var(--lp-text, #26332b);
  background: var(--lp-surface, #fff);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.5rem;
}
.create-ws-input:focus,
.create-ws-textarea:focus {
  border-color: var(--lp-primary, #4aa46a);
  background: var(--lp-surface, #fff);
  box-shadow: none;
}
.create-ws-input::placeholder,
.create-ws-textarea::placeholder {
  color: var(--lp-text-3, #94a59b);
}
.create-ws-hint {
  color: var(--lp-text-3, #94a59b);
}

/* —— 底部按钮区 —— */
.create-ws-cancel-btn {
  color: var(--lp-text-2, #5f7268);
  background: var(--lp-surface, #fff);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.5rem;
}
.create-ws-cancel-btn:hover {
  color: var(--lp-primary-dark, #2f7a4a);
  border-color: var(--lp-primary-soft2, #d5ebdb);
  background: var(--lp-primary-soft, #e9f5ec);
}
.create-ws-confirm-btn {
  color: #fff;
  background: var(--lp-primary, #4aa46a);
  border: 2px solid var(--lp-primary, #4aa46a);
  border-radius: 0.5rem;
}
.create-ws-confirm-btn:hover:not(:disabled) {
  background: var(--lp-primary-dark, #2f7a4a);
  border-color: var(--lp-primary-dark, #2f7a4a);
}

/* —— 图标类型单选 —— */
.create-ws-radio-circle {
  border: 2px solid var(--lp-border, #e3efe6);
}
.create-ws-radio-option:hover .create-ws-radio-circle,
.create-ws-radio-option.active .create-ws-radio-circle {
  border-color: var(--lp-primary, #4aa46a);
}
.create-ws-radio-dot {
  background: var(--lp-primary, #4aa46a);
}
.create-ws-radio-label {
  color: var(--lp-text-2, #5f7268);
}
.create-ws-radio-option.active .create-ws-radio-label {
  color: var(--lp-primary-dark, #2f7a4a);
}

/* —— 预设图标网格 ——
   选中态 = **仅浅底色**（--dot-color-soft），不画任何边框/描边：
   用户最新要求"去掉图标网格选中后的边框"，所以去掉了原来的 2px solid outline，
   只保留 soft 浅底让选中格像"选中 chip"一样可辨。
   显式 box-shadow:none 仍要留着 —— 清掉原样式块里的蓝色实线 box-shadow，
   否则选中时会冒出一圈蓝色。
   --dot-color-dark 现在只被色板圆圈（2px dashed 虚线）消费，图标格不再用。 */
.create-ws-icon-option.active {
  box-shadow: none;
  background: var(--dot-color-soft, #eaf3de);
}

/* —— 预设图标 / 首字母 / 图片预览 统一外框 ——
   把下面三类「图标区」框成同一种视觉容器：浅色底 + 2px 实线描边 +
   0.5rem 圆角 + 12px 内边距。预设网格原本无边框/内边距，这里补上，
   让它和图片、字母区看起来是同一档次的「外框」。
   ⚠️ 宽度是算出来的：网格 11×32 + 10×12 = 472px，加 12×2 padding +
   2×2 border = 500px，恰好等于弹窗内容区可用宽（560 − 30×2），不溢出。 */
.create-ws-icon-letter-preview,
.create-ws-icon-image-preview,
.create-ws-icon-grid {
  background: var(--lp-surface-2, #f7fbf8);
  border: 2px solid var(--lp-border, #e3efe6);
  border-radius: 0.5rem;
  padding: 12px;
}

/* 预设网格只有 32px 高的单行，而图片/字母区因内含 48px 头像/字母块，
   外框高为 48 + 12×2 padding + 2×2 border = 76px。给网格补 min-height:76px
   并把单行在框内垂直居中（align-content），让三个外框高度一致。
   外框与底色透明，但保留边框占位 / padding / min-height，确保高度和布局不变。 */
.create-ws-icon-grid {
  align-content: center;
  min-height: 76px;
  background: transparent;
  border-color: transparent;
}
.create-ws-icon-letter-hint,
.create-ws-image-hint {
  color: var(--lp-text-3, #94a59b);
}
.create-ws-image-avatar {
  border: 2px solid var(--lp-border, #e3efe6);
}
.create-ws-image-avatar:hover {
  border-color: var(--lp-primary, #4aa46a);
}
.create-ws-image-avatar-empty {
  background: var(--lp-surface, #fff);
  border: 2px dashed var(--lp-border, #e3efe6);
}
.create-ws-image-avatar-icon {
  color: var(--lp-text-3, #94a59b);
}
.create-ws-image-avatar-overlay {
  background: rgba(38, 51, 43, 0.45);
}

/* —— 色系选择器（圆圈色点）——
   选中态 = 白色内圈 + **虚线外圈**，虚线色取该色自己的 dark 派生色
   （--dot-color-dark 由模板注入），而不是全局 --lp-primary：10 种色里只有
   1 种是绿色，统一用绿色外圈会让其余 9 种的选中态显得违和。
   box-shadow 画不出虚线，改用 outline（唯一支持 dashed 的描边属性），
   并显式清掉原样式块的实线 box-shadow。图标 offset 1px（折中值），
   色点按 32/56≈0.57 比例取 0.5px，跟图标节奏同步。 */
.create-ws-color-dot.active {
  border-color: #fff;
  box-shadow: none;
  outline: 2px dashed var(--dot-color-dark, var(--lp-primary, #4aa46a));
  outline-offset: 0.5px;
}
</style>
